import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, Package, PackageCheck, Truck, Home, MapPin, Copy, ArrowRight, Loader2, AlertCircle, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { CONTACT_INFO } from "@/data/anabiya";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_ICONS = {
  pending: Package,
  processing: PackageCheck,
  shipped: Truck,
  out_for_delivery: Truck,
  delivered: Home,
};

const useQuery = () => new URLSearchParams(useLocation().search);

const TrackOrderPage = () => {
  useSmoothScroll();
  const nav = useNavigate();
  const qs = useQuery();
  const initial = qs.get("q") || qs.get("id") || "";
  const [query, setQuery] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runSearch = async (raw) => {
    const q = (raw ?? query).trim();
    if (!q) { setError("Please enter your Order ID or Tracking number."); return; }
    setLoading(true); setError(""); setData(null);
    try {
      const { data } = await axios.get(`${API}/track-order`, { params: { q } });
      setData(data);
      // reflect in URL so the result is shareable
      nav(`/track-order?q=${encodeURIComponent(q)}`, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initial) runSearch(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main data-testid="track-order-page" className="bg-black text-white min-h-screen">
      <Seo path="/track-order" title="Track Your Order · Anabiya Decor" description="Track your Anabiya Decor order using your Order ID or courier tracking number. Real-time delivery updates from our atelier to your door." />
      <AnnouncementBar />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          aria-hidden
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(1200px 500px at 20% 10%, rgba(212,175,55,0.14), transparent 60%), radial-gradient(900px 500px at 90% 100%, rgba(212,175,55,0.10), transparent 60%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 py-14 md:py-20 text-center">
          <div className="overline text-[hsl(45_60%_60%)] tracking-[3px] text-[11px] mb-4">§ Order Concierge</div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
          >
            Track Your Order — <span className="italic text-[hsl(45_60%_60%)]">Anabiya Decor</span>
          </motion.h1>
          <p className="mt-4 text-white/70 text-sm md:text-base max-w-xl mx-auto">
            Enter your Order ID (e.g. <span className="tabnum text-white">AN-XXXXXXXXXX</span>) or the courier tracking / AWB number we shared over WhatsApp.
          </p>

          {/* Search bar */}
          <form
            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto"
          >
            <input
              data-testid="track-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter Your Order ID or Tracking ID"
              className="flex-1 bg-white/[0.04] border border-white/15 focus:border-[hsl(45_60%_60%)] outline-none rounded-full px-5 py-3 text-white placeholder:text-white/40 tracking-wide"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="track-submit"
              className="inline-flex items-center justify-center gap-2 bg-[hsl(45_60%_55%)] text-black overline tracking-[3px] rounded-full px-6 py-3 disabled:opacity-60 hover:bg-[hsl(45_65%_60%)] transition-colors"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} strokeWidth={2} />}
              {loading ? "Searching…" : "Track Now"}
            </button>
          </form>

          {error && (
            <div data-testid="track-error" className="mt-6 max-w-2xl mx-auto flex items-start gap-3 bg-red-500/10 border border-red-400/30 text-red-200 rounded-lg px-4 py-3 text-sm text-left">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}
        </div>
      </section>

      {/* Result */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-12 md:py-16">
        {data ? (
          <TrackingResult data={data} />
        ) : !loading && !error ? (
          <EmptyState />
        ) : null}
      </section>

      <Footer />
    </main>
  );
};

const EmptyState = () => (
  <div className="border border-white/10 bg-white/[0.02] rounded-2xl p-10 md:p-14 text-center">
    <Package size={32} strokeWidth={1.4} className="mx-auto text-[hsl(45_60%_60%)] mb-4" />
    <h2 className="font-display text-2xl md:text-3xl">Your order journey — traced end to end.</h2>
    <p className="mt-3 text-white/60 max-w-md mx-auto text-sm">
      Every Anabiya piece is hand-finished in Mumbai and dispatched with tracked international couriers. Enter your ID above to see live status.
    </p>
    <Link to="/shop" className="inline-flex items-center gap-2 mt-6 overline tracking-[3px] text-[hsl(45_60%_60%)] hover:text-white transition-colors">
      Continue shopping <ArrowRight size={13} />
    </Link>
  </div>
);

const TrackingResult = ({ data }) => {
  const currentIndex = useMemo(() => {
    const idx = (data.steps || []).findIndex((s) => s.key === data.status);
    return idx >= 0 ? idx : 0;
  }, [data]);

  const isCancelled = data.status === "cancelled";
  const eta = data.estimated_delivery ? new Date(data.estimated_delivery) : null;
  const etaFormatted = eta && !isNaN(eta) ? eta.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";

  const copy = (v, label) => {
    if (!v) return;
    navigator.clipboard?.writeText(v);
    toast.success(`${label} copied`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="track-result"
      className="space-y-8"
    >
      {/* Summary card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 border border-white/10 bg-white/[0.02] rounded-2xl p-6 md:p-8">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="overline text-[hsl(45_60%_60%)] tracking-[3px] text-[10px]">§ Current Status</div>
              <h2 className="font-display text-3xl md:text-4xl mt-1 leading-none">
                {isCancelled ? "Order Cancelled" : (data.steps.find((s) => s.key === data.status)?.label || "Order Placed")}
              </h2>
              <div className="mt-2 text-white/60 text-sm">
                Order <span className="tabnum text-white">{data.order_id}</span>
                {data.buyer_name ? <> · {data.buyer_name}</> : null}
              </div>
            </div>
            <StatusPill status={data.status} />
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Fact label="Placed" value={data.created_at ? new Date(data.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"} />
            <Fact label="Payment" value={(data.payment_status || "pending").replace(/_/g, " ")} />
            <Fact label="Items" value={String(data.item_count || 0)} />
            <Fact label="Est. Delivery" value={etaFormatted || "TBD"} highlight={!!etaFormatted} />
          </div>
        </div>

        {/* Courier card */}
        <div className="border border-[hsl(45_60%_55%)]/40 bg-gradient-to-br from-[hsl(45_60%_55%)]/[0.10] to-transparent rounded-2xl p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="overline text-[hsl(45_60%_60%)] tracking-[3px] text-[10px]">§ Courier</div>
            {data.live_source && (
              <span
                data-testid="track-live-badge"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-400/15 text-emerald-200 border border-emerald-400/40 px-2 py-0.5 text-[10px] tracking-[2px] uppercase"
                title={`Scans pulled from ${data.live_courier?.toUpperCase() || "carrier"} (${data.live_source})`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" /> Live · {data.live_courier?.toUpperCase()}
              </span>
            )}
          </div>
          <div className="mt-1 font-display text-2xl leading-tight">
            {data.courier_name || (data.live_courier ? data.live_courier.toUpperCase() : "Assignment pending")}
          </div>
          {data.tracking_number ? (
            <>
              <div className="mt-4 text-white/60 text-xs uppercase tracking-widest">Tracking / AWB</div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <div data-testid="track-awb" className="font-display tabnum text-lg text-white truncate">{data.tracking_number}</div>
                <button
                  onClick={() => copy(data.tracking_number, "Tracking number")}
                  className="p-2 rounded-full border border-white/15 hover:bg-white/10"
                  aria-label="Copy tracking number"
                  data-testid="track-copy-awb"
                >
                  <Copy size={12} />
                </button>
              </div>
            </>
          ) : (
            <p className="mt-4 text-white/60 text-sm leading-relaxed">
              Your parcel is being prepared. We'll drop the tracking number over WhatsApp the moment it dispatches.
            </p>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="border border-white/10 bg-white/[0.02] rounded-2xl p-6 md:p-10">
        <div className="overline text-[hsl(45_60%_60%)] tracking-[3px] text-[10px] mb-6">§ Delivery Timeline</div>
        {isCancelled ? (
          <div className="text-red-300/90 text-sm">This order has been cancelled. If this is unexpected, please reach us on WhatsApp.</div>
        ) : (
          <Timeline steps={data.steps} currentIndex={currentIndex} history={data.history} />
        )}
      </div>

      {/* Items */}
      {data.items?.length > 0 && (
        <div className="border border-white/10 bg-white/[0.02] rounded-2xl p-6 md:p-8">
          <div className="overline text-[hsl(45_60%_60%)] tracking-[3px] text-[10px] mb-4">§ In this order</div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.items.map((it, i) => (
              <li key={i} className="flex gap-3 items-center border border-white/10 bg-black/40 rounded-xl p-3">
                {it.image ? (
                  <img src={it.image} alt={it.title ? `${it.title} — order item` : "Order item"} loading="lazy" decoding="async" className="w-14 h-14 rounded-md object-cover border border-white/10" />
                ) : (
                  <div className="w-14 h-14 rounded-md bg-white/5 border border-white/10" />
                )}
                <div className="min-w-0">
                  <div className="font-display text-base truncate">{it.title}</div>
                  <div className="text-white/50 text-xs mt-0.5">
                    {[it.size, `× ${it.qty}`].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {data.item_count > data.items.length && (
            <div className="mt-4 text-white/50 text-xs">+ {data.item_count - data.items.length} more item(s)</div>
          )}
        </div>
      )}

      {/* Help CTA */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="font-display text-xl">Need help with this order?</div>
          <div className="text-white/60 text-sm mt-1">Our atelier team replies within an hour.</div>
        </div>
        <a
          data-testid="track-whatsapp"
          href={`https://wa.me/${(CONTACT_INFO?.whatsapp || "919000000000")}?text=${encodeURIComponent(`Hi Anabiya, I need help with order ${data.order_id}.`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[hsl(45_60%_55%)] text-black overline tracking-[3px] rounded-full px-5 py-3 hover:bg-[hsl(45_65%_60%)] transition-colors"
        >
          <MessageCircle size={13} /> WhatsApp us
        </a>
      </div>
    </motion.div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    pending: { c: "bg-white/10 text-white", label: "Placed" },
    processing: { c: "bg-amber-400/20 text-amber-200", label: "Packed" },
    shipped: { c: "bg-sky-400/20 text-sky-200", label: "Shipped" },
    out_for_delivery: { c: "bg-fuchsia-400/20 text-fuchsia-200", label: "Out for Delivery" },
    delivered: { c: "bg-emerald-400/20 text-emerald-200", label: "Delivered" },
    cancelled: { c: "bg-red-400/20 text-red-200", label: "Cancelled" },
  };
  const s = map[status] || map.pending;
  return (
    <span data-testid="track-status-pill" className={`shrink-0 rounded-full px-3 py-1 overline tracking-[3px] text-[10px] ${s.c}`}>
      {s.label}
    </span>
  );
};

const Fact = ({ label, value, highlight }) => (
  <div>
    <div className="overline text-white/45 tracking-[2px] text-[10px]">{label}</div>
    <div className={`mt-1 font-display ${highlight ? "text-[hsl(45_60%_65%)]" : "text-white"}`}>{value}</div>
  </div>
);

const Timeline = ({ steps, currentIndex, history }) => {
  // History lookup: latest entry per canonical status
  const lastFor = useMemo(() => {
    const out = {};
    (history || []).forEach((h) => { out[h.status] = h; });
    return out;
  }, [history]);

  return (
    <ol className="relative" data-testid="track-timeline">
      {steps.map((s, i) => {
        const Icon = STEP_ICONS[s.key] || Package;
        const done = i < currentIndex;
        const active = i === currentIndex;
        const upcoming = i > currentIndex;
        const h = lastFor[s.key];
        const at = h?.at ? new Date(h.at) : null;
        return (
          <li key={s.key} className="relative flex items-start gap-4 pb-8 last:pb-0" data-testid={`track-step-${s.key}`}>
            {/* Vertical connector */}
            {i < steps.length - 1 && (
              <span
                aria-hidden
                className={`absolute left-[19px] top-10 w-[2px] h-[calc(100%-2rem)] ${done ? "bg-[hsl(45_60%_55%)]" : "bg-white/10"}`}
              />
            )}
            {/* Node */}
            <div
              className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center border transition-colors ${
                done
                  ? "bg-[hsl(45_60%_55%)] text-black border-[hsl(45_60%_55%)]"
                  : active
                  ? "bg-black text-[hsl(45_60%_60%)] border-[hsl(45_60%_55%)] shadow-[0_0_0_4px_rgba(212,175,55,0.15)]"
                  : "bg-black/40 text-white/40 border-white/15"
              }`}
            >
              <Icon size={16} strokeWidth={1.8} />
              {active && (
                <span className="absolute inset-0 rounded-full animate-ping bg-[hsl(45_60%_55%)]/30" />
              )}
            </div>

            {/* Body */}
            <div className="pt-1 min-w-0 flex-1">
              <div className={`font-display text-lg leading-none ${upcoming ? "text-white/50" : "text-white"}`}>{s.label}</div>
              <div className="mt-1 text-xs text-white/50">
                {at
                  ? at.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
                  : active
                  ? "In progress"
                  : done
                  ? "Completed"
                  : "Pending"}
                {h?.location ? <span className="text-white/60"> · {h.location}</span> : null}
                {h?.note ? <span className="text-white/40"> · {h.note}</span> : null}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default TrackOrderPage;
